import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ClassificationsSection from './ClassificationsSection';
import ClassificationAttachmentsSection from './ClassificationAttachmentsSection';
import AddClassificationSuccessScreen from './AddClassificationSuccessScreen';
import ClassificationConfirmationPopup from '@/components/ui/ClassificationConfirmationPopup/ClassificationConfirmationPopup';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import type { ClassificationWithLimitsWithExistingFlagDtoIn } from '@/models/in/ClassificationWithLimitsWithExistingFlagDtoIn';
import type { ContactClassificationDtoOut } from '@/models/out/ContactClassificationDtoOut';
import styles from './AddClassificationPopup.module.scss';

interface AddClassificationPopupProps {
  fundId: number;
  supportedId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  onRefreshList?: () => void;
  /**
   * HAT-1453: when given, the success screen offers "go to a support request" for this supported.
   * The popup closes first, so the request dialog is not stacked on it.
   */
  onGoToRequest?: () => void;
}

type RequiredDocument = {
  file: File;
  requiredDocName: string;
  classificationId: number;
};

type AdditionalDocument = {
  file: File | null;
  customDescription: string;
  classificationId: number;
};

const AddClassificationPopup = ({ fundId, supportedId, open, setOpen, onRefreshList, onGoToRequest }: AddClassificationPopupProps) => {
  const { t } = useTranslation();
  const [selectedClassificationIds, setSelectedClassificationIds] = useState<number[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocument[]>([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<AdditionalDocument[]>([]);
  const [comments, setComments] = useState<Record<number, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false);

  const apiUrl = open ? `funds/${fundId}/supporteds/${supportedId}/available-classifications` : '';
  const { data: classificationsData, refetch: refetchClassifications } = useGetAuthApi<ClassificationWithLimitsWithExistingFlagDtoIn[]>(apiUrl);

  const { mutate: submitClassifications, isPending: isSubmitting } = usePostAuthApi<void, ContactClassificationDtoOut[]>(
    `funds/${fundId}/supporteds/${supportedId}/classifications`
  );

  // Reset state when popup closes
  useEffect(() => {
    if (!open) {
      setSelectedClassificationIds([]);
      setRequiredDocuments([]);
      setAdditionalDocuments([]);
      setComments({});
      setIsSuccess(false);
      setIsConfirmationPopupOpen(false);
    }
  }, [open]);

  const handleToggle = (classificationId: number) => {
    setSelectedClassificationIds((prev) => {
      if (prev.includes(classificationId)) {
        // Remove classification and its documents and comments
        setRequiredDocuments((docs) => docs.filter((doc) => doc.classificationId !== classificationId));
        setAdditionalDocuments((docs) => docs.filter((doc) => doc.classificationId !== classificationId));
        setComments((prev) => {
          const { [classificationId]: _, ...rest } = prev;
          return rest;
        });
        return prev.filter((id) => id !== classificationId);
      } else {
        // Add classification
        return [...prev, classificationId];
      }
    });
  };

  const handleCommentsChange = (classificationId: number, value: string) => {
    setComments((prev) => ({
      ...prev,
      [classificationId]: value,
    }));
  };

  const handleRequiredDocumentChange = (classificationId: number, requiredDoc: string, file: File | null) => {
    setRequiredDocuments((prev) => {
      const filteredDocs = prev.filter((doc) => !(doc.classificationId === classificationId && doc.requiredDocName === requiredDoc));
      return file ? [...filteredDocs, { file, requiredDocName: requiredDoc, classificationId }] : filteredDocs;
    });
  };

  const handleAddAdditionalDocument = (classificationId: number) => {
    setAdditionalDocuments((prev) => [...prev, { file: null, customDescription: '', classificationId }]);
  };

  const handleRemoveAdditionalDocument = (classificationId: number, docIndex: number) => {
    setAdditionalDocuments((prev) => {
      const classificationDocs = prev.filter((doc) => doc.classificationId === classificationId);
      const docToRemove = classificationDocs[docIndex];
      return prev.filter((doc) => doc !== docToRemove);
    });
  };

  const handleAdditionalDocumentChange = (classificationId: number, docIndex: number, value: { file: File | null; customDescription: string }) => {
    setAdditionalDocuments((prev) => {
      const classificationDocs = prev.filter((doc) => doc.classificationId === classificationId);
      const docToUpdate = classificationDocs[docIndex];
      const actualIndex = prev.indexOf(docToUpdate);

      if (actualIndex === -1) return prev;

      const updated = [...prev];
      updated[actualIndex] = {
        ...updated[actualIndex],
        file: value.file,
        customDescription: value.customDescription,
      };
      return updated;
    });
  };

  const validateSubmission = (): string | null => {
    // Check if at least one classification is selected
    if (selectedClassificationIds.length === 0) {
      return t('selectAtLeastOneClassification');
    }

    // Check if each selected classification with required documents has at least one document uploaded
    for (const classificationId of selectedClassificationIds) {
      const classification = classificationsData?.find((item) => item.classificationWithLimits.classification.id === classificationId)?.classificationWithLimits
        .classification;

      if (classification?.requiredDocuments && classification.requiredDocuments.length > 0) {
        const classificationRequiredDocs = requiredDocuments.filter((doc) => doc.classificationId === classificationId);
        if (classificationRequiredDocs.length === 0) {
          return t('uploadRequiredDocuments');
        }
      }
    }

    return null;
  };

  const handleSubmit = () => {
    // Validate
    const validationError = validateSubmission();
    if (validationError) {
      alert(validationError);
      return;
    }

    // Open confirmation popup
    setIsConfirmationPopupOpen(true);
  };

  const handleFinalSubmit = async () => {
    try {
      // Convert files to base64 and create the payload
      const classificationsToSubmit: ContactClassificationDtoOut[] = [];

      for (const classificationId of selectedClassificationIds) {
        const classificationRequiredDocs = requiredDocuments.filter((doc) => doc.classificationId === classificationId);
        const classificationAdditionalDocs = additionalDocuments.filter((doc) => doc.classificationId === classificationId);

        // Convert required documents
        // Note: requiredDocuments never have null files
        const convertedRequiredDocs = await Promise.all(
          classificationRequiredDocs.map((doc) => FileReaderHelper.fileToDocumentDtoOut(doc.file, doc.requiredDocName))
        );

        // Convert additional documents
        const convertedAdditionalDocs = await Promise.all(
          classificationAdditionalDocs.filter((doc) => doc.file !== null).map((doc) => FileReaderHelper.fileToDocumentDtoOut(doc.file!, doc.customDescription))
        );

        // Combine all documents
        const allDocs = [...convertedRequiredDocs.flat(), ...convertedAdditionalDocs.flat()];

        classificationsToSubmit.push({
          classificationId,
          documents: allDocs,
          comments: comments[classificationId] || undefined,
        });
      }

      // Submit to API
      submitClassifications(classificationsToSubmit, {
        onSuccess: () => {
          // Refresh all data on success
          refetchClassifications(); // Refresh available classifications for popup
          if (onRefreshList) {
            onRefreshList(); // Refresh supporteds list/data
          }
          setIsConfirmationPopupOpen(false);
          setIsSuccess(true);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data || error?.message || t('errorCreatingSupported');
          alert(errorMessage);
        },
      });
    } catch (error: any) {
      alert(error?.message || t('errorCreatingSupported'));
    }
  };

  const handleBackToSupporteds = () => {
    setIsSuccess(false);
    setOpen(false);
  };

  const handleGoToRequest = () => {
    setIsSuccess(false);
    setOpen(false);
    onGoToRequest?.();
  };

  const handleAddAnotherClassification = () => {
    setIsSuccess(false);
    setSelectedClassificationIds([]);
    setRequiredDocuments([]);
    setAdditionalDocuments([]);
    setComments({});
  };

  const getSelectedClassificationNames = (): string[] => {
    return selectedClassificationIds.map((id) => {
      const classification = classificationsData?.find((item) => item.classificationWithLimits.classification.id === id)?.classificationWithLimits
        .classification;
      return t(classification?.name || '');
    });
  };

  const isSubmitDisabled = selectedClassificationIds.length === 0 || isSubmitting;

  // Filter out Basic classifications for documents section
  const nonBasicSelectedClassificationIds = selectedClassificationIds.filter((classificationId) => {
    const classification = classificationsData?.find((item) => item.classificationWithLimits.classification.id === classificationId)?.classificationWithLimits
      .classification;
    return classification?.name !== 'Basic';
  });

  return (
    <MainDialogLayout open={open} onOpenChange={setOpen} title={`${t('addNewClassification')}`}>
      {isSuccess ? (
        <AddClassificationSuccessScreen onBackToSupporteds={handleBackToSupporteds} onAddAnotherClassification={handleAddAnotherClassification}
          onGoToRequest={onGoToRequest ? handleGoToRequest : undefined}
        />
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <ClassificationsSection classificationsData={classificationsData || []} selectedIds={selectedClassificationIds} onToggle={handleToggle} />

            {nonBasicSelectedClassificationIds.length > 0 && (
              <div>
                <h4 className="body-lr-bold text-(--font-sub-title-2) pb-4">{t('uploadDocumentsAndApprovals')}</h4>
                {nonBasicSelectedClassificationIds.map((classificationId) => {
                  const classification = classificationsData?.find((item) => item.classificationWithLimits.classification.id === classificationId)
                    ?.classificationWithLimits.classification;

                  return (
                    <ClassificationAttachmentsSection
                      key={classificationId}
                      classification={classification}
                      requiredDocuments={requiredDocuments.filter((doc) => doc.classificationId === classificationId)}
                      additionalDocuments={additionalDocuments.filter((doc) => doc.classificationId === classificationId)}
                      onRequiredDocumentChange={(requiredDoc, file) => handleRequiredDocumentChange(classificationId, requiredDoc, file)}
                      onAddAdditionalDocument={() => handleAddAdditionalDocument(classificationId)}
                      onRemoveAdditionalDocument={(docIndex) => handleRemoveAdditionalDocument(classificationId, docIndex)}
                      onAdditionalDocumentChange={(docIndex, value) => handleAdditionalDocumentChange(classificationId, docIndex, value)}
                      comments={comments[classificationId] || ''}
                      onCommentsChange={(value) => handleCommentsChange(classificationId, value)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <button onClick={handleSubmit} disabled={isSubmitDisabled} className={isSubmitDisabled ? styles.submitButtonDisabled : styles.submitButton}>
              {isSubmitting ? t('submitting') : t('submitClassificationRequest')}
            </button>
          </div>
        </div>
      )}
      <ClassificationConfirmationPopup
        open={isConfirmationPopupOpen}
        setOpen={setIsConfirmationPopupOpen}
        selectedCount={selectedClassificationIds.length}
        onSubmit={handleFinalSubmit}
        isSubmitting={isSubmitting}
        classificationNames={getSelectedClassificationNames()}
      />
    </MainDialogLayout>
  );
};

export default AddClassificationPopup;
